'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { resolveTenantSlug, isAdminSubdomain, extractClientSlugFromPath } from '@/lib/tenant/resolveTenant';
import { getOrgForSlug } from '@/lib/tenant/getOrgForSlug';
import type { Org } from '@/lib/firebase/types';

interface TenantContextValue {
  slug: string;
  org: Org | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  isAdminDomain: boolean;
  switchTenant: (slug: string) => void;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const [slug, setSlug] = useState<string>('');
  const [org, setOrg] = useState<Org | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [isAdminDomain, setIsAdminDomain] = useState(false);
  const router = useRouter();
  const pathname = usePathname(); // re-run fetchOrg when route changes

  const fetchOrg = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (typeof window === 'undefined') return;

      const currentPathname = window.location.pathname;
      const hostname = window.location.hostname;

      // Skip tenant resolution for marketing routes (/m/*)
      if (currentPathname.startsWith('/m/') || currentPathname === '/m') {
        setSlug('');
        setOrg(null);
        setLoading(false);
        return;
      }

      // Admin subdomain: resolve tenant from URL path instead of hostname
      if (isAdminSubdomain(hostname)) {
        setIsAdminDomain(true);
        const clientSlug = extractClientSlugFromPath(currentPathname);
        console.log('[TenantProvider] admin domain, pathname:', currentPathname, '→ slug:', clientSlug);
        setSlug(clientSlug);
        if (clientSlug) {
          const orgData = await getOrgForSlug(clientSlug);
          console.log('[TenantProvider] fetched org:', orgData?.name ?? 'null');
          setOrg(orgData);
        } else {
          setOrg(null);
        }
        setLoading(false);
        return;
      }

      setIsAdminDomain(false);

      // Normal subdomain: resolve from hostname
      const resolvedSlug = resolveTenantSlug(hostname);
      setSlug(resolvedSlug);

      if (!resolvedSlug) {
        setOrg(null);
        setLoading(false);
        return;
      }

      const orgData = await getOrgForSlug(resolvedSlug);
      setOrg(orgData);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch tenant');
      console.error('[TenantProvider] error:', error);
      setError(error);
      setOrg(null);
    } finally {
      setLoading(false);
    }
  }, [pathname]); // re-run when pathname changes (critical for admin domain tenant switching)

  useEffect(() => {
    fetchOrg();
  }, [fetchOrg]);

  const refresh = useCallback(async () => {
    await fetchOrg();
  }, [fetchOrg]);

  const switchTenant = useCallback((newSlug: string) => {
    console.log('[TenantProvider] switchTenant →', newSlug);
    router.push(`/admin/${newSlug}/zones`);
  }, [router]);

  return (
    <TenantContext.Provider value={{ slug, org, loading, error, refresh, isAdminDomain, switchTenant }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
